import { useEffect, useRef, useState, type InputHTMLAttributes } from "react";
import type { AssetTypeDef, SlotDef } from "../../data/types";
import { slugify, uniqueSlug } from "../../data/slug";
import { Button, Display } from "../../design/primitives";
import { useStudio } from "./StudioContext";
import css from "./studio.module.css";

/**
 * The file formats each asset type offers — `editable-file-slots`.
 *
 * A tab offers a download for every slot here that it holds a file for, so
 * this list is what decides "PDF, GP and MIDI on this one; PDF only on that".
 *
 * **A slot id never changes once saved** (FR-10). It is part of every S3 key
 * and every matrix key, so renaming one would orphan every file stored under
 * it. The id is generated from the short name while the slot is new, then
 * fixed, and never shown. Slots retire instead of deleting (D-12). The server
 * enforces both (FR-11); this screen only makes them hard to get wrong.
 *
 * Edits go into the Studio record like every other list here — nothing is sent
 * until SAVE CHANGES, and a refusal lands in the header (FR-8).
 */

const MB = 1024 * 1024;

/** FR-4: "MID, .midi" -> [".mid", ".midi"]. */
export function parseExtensions(text: string): string[] {
  return text
    .split(",")
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean)
    .map((e) => (e.startsWith(".") ? e : `.${e}`));
}

/** FS-D8: the short name slugged, unique in the type, never empty. */
export function slotIdFor(shortName: string, taken: Iterable<string>): string {
  return uniqueSlug(slugify(shortName) || "slot", taken);
}

/**
 * Text the creator is typing, committed as parsed data on every keystroke.
 *
 * Holds its own string because the parsed value cannot round-trip a half-typed
 * one — "mid," would come back as ".mid" and eat the comma. It resyncs from
 * the record whenever the record changes underneath it (DISCARD, reorder).
 */
function DraftInput({
  value,
  parse,
  format,
  onCommit,
  ...rest
}: {
  value: unknown;
  parse: (text: string) => { ok: true; value: unknown } | { ok: false };
  format: (value: unknown) => string;
  onCommit: (value: unknown) => void;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange">) {
  const [text, setText] = useState(format(value));
  const committed = useRef(value);
  useEffect(() => {
    if (JSON.stringify(value) !== JSON.stringify(committed.current)) {
      committed.current = value;
      setText(format(value));
    }
  }, [value, format]);
  return (
    <input
      {...rest}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const parsed = parse(e.target.value);
        if (parsed.ok) {
          committed.current = parsed.value;
          onCommit(parsed.value);
        }
      }}
    />
  );
}

const formatExtensions = (v: unknown) => (v as string[]).join(", ");
const parseExtensionsResult = (t: string) => ({ ok: true as const, value: parseExtensions(t) });

const formatMb = (v: unknown) => (v === undefined ? "" : String(Math.round((v as number) / MB)));
/**
 * FR-5: whole megabytes or blank. A number out of 1-100 is still committed,
 * so the server's refusal names it (AC-6); only text that is not a whole
 * number is held back.
 */
const parseMb = (t: string) =>
  t.trim() === ""
    ? { ok: true as const, value: undefined }
    : /^\d+$/.test(t.trim())
      ? { ok: true as const, value: Number(t.trim()) * MB }
      : { ok: false as const };

export function SlotEditor() {
  const { creator, update, dirty } = useStudio();

  /*
   * Slots added since the last save, as "type:id". Only these have an id
   * that still follows the short name. A save or DISCARD clears `dirty`, and
   * with it this set: the ids are now stored, or gone.
   */
  const [fresh, setFresh] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!dirty) setFresh(new Set());
  }, [dirty]);

  /*
   * React keys that survive a fresh slot's id changing under the typing
   * cursor. Keyed by id alone, every keystroke in SHORT NAME would remount
   * the row and drop focus.
   */
  const rowKeys = useRef(new Map<string, string>());
  const keyFor = (type: string, id: string) => rowKeys.current.get(`${type}:${id}`) ?? `${type}:${id}`;

  const setSlots = (type: AssetTypeDef, slots: SlotDef[]) =>
    update({
      ...creator,
      assetTypes: creator.assetTypes.map((t) => (t.type === type.type ? { ...t, slots } : t)),
    });

  const patchSlot = (type: AssetTypeDef, index: number, patch: Partial<SlotDef>) => {
    const slots = [...type.slots];
    slots[index] = { ...slots[index], ...patch };
    setSlots(type, slots);
  };

  const setShortName = (type: AssetTypeDef, index: number, shortName: string) => {
    const slot = type.slots[index];
    const freshKey = `${type.type}:${slot.id}`;
    if (!fresh.has(freshKey)) return patchSlot(type, index, { shortName });

    const others = type.slots.filter((_, i) => i !== index).map((s) => s.id);
    const id = slotIdFor(shortName, others);
    if (id !== slot.id) {
      const nextKey = `${type.type}:${id}`;
      rowKeys.current.set(nextKey, keyFor(type.type, slot.id));
      rowKeys.current.delete(freshKey);
      setFresh((f) => new Set([...f].filter((k) => k !== freshKey)).add(nextKey));
    }
    patchSlot(type, index, { shortName, id });
  };

  const addSlot = (type: AssetTypeDef) => {
    const id = slotIdFor("", type.slots.map((s) => s.id));
    setFresh((f) => new Set(f).add(`${type.type}:${id}`));
    setSlots(type, [...type.slots, { id, label: "", shortName: "", accept: [], required: false }]);
  };

  const move = (type: AssetTypeDef, index: number, by: -1 | 1) => {
    const to = index + by;
    if (to < 0 || to >= type.slots.length) return;
    const slots = [...type.slots];
    [slots[index], slots[to]] = [slots[to], slots[index]];
    setSlots(type, slots);
  };

  return (
    <section className={css.listSection}>
      <Display size={21} as="h2" style={{ letterSpacing: "var(--track-section-header)" }}>
        ASSET TYPES
      </Display>
      <p className={css.note}>
        The file formats each type can carry. An asset offers a download for
        every format here that it has a file for. Retire a format rather than
        removing it: files already uploaded keep working.
      </p>

      {creator.assetTypes.map((type) => (
        <div key={type.type} className={css.slotType}>
          <h3 className={css.label}>
            {type.label.toUpperCase()}
            {type.retired && " · RETIRED"}
          </h3>

          <div className={`${css.slotRow} ${css.slotHead}`} aria-hidden="true">
            <span>LABEL</span>
            <span>SHORT NAME</span>
            <span>EXTENSIONS</span>
            <span>MAX MB</span>
            <span>REQUIRED</span>
            <span>ORDER</span>
            <span />
          </div>

          {type.slots.map((slot, i) => {
            const isFresh = fresh.has(`${type.type}:${slot.id}`);
            const rowKey = keyFor(type.type, slot.id);

            // Cover art: stored without a short name, owned by
            // `asset-cover-art`, and not a download (FR-2).
            if (slot.shortName === undefined && !isFresh) {
              return (
                <div key={rowKey} className={css.slotRow} data-slot={slot.id}>
                  <span>{slot.label}</span>
                  <span className={css.inUse}>—</span>
                  <span className={css.inUse}>{slot.accept.join(", ")}</span>
                  <span />
                  <span />
                  <span />
                  <span className={css.inUse}>COVER ART · NOT A DOWNLOAD</span>
                </div>
              );
            }

            const mbInvalid =
              slot.maxBytes !== undefined && !(slot.maxBytes >= MB && slot.maxBytes <= 100 * MB);

            return (
              <div
                key={rowKey}
                className={`${css.slotRow} ${slot.retired ? css.listRowRetired : ""}`}
                data-slot={slot.id}
              >
                <input
                  className={css.input}
                  aria-label="Slot label"
                  placeholder="Tab — MIDI"
                  value={slot.label}
                  onChange={(e) => patchSlot(type, i, { label: e.target.value })}
                />
                <input
                  className={css.input}
                  aria-label="Slot short name"
                  placeholder="MIDI"
                  value={slot.shortName ?? ""}
                  onChange={(e) => setShortName(type, i, e.target.value)}
                />
                <DraftInput
                  className={css.input}
                  aria-label="Slot extensions"
                  placeholder=".mid, .midi"
                  value={slot.accept}
                  format={formatExtensions}
                  parse={parseExtensionsResult}
                  onCommit={(accept) => patchSlot(type, i, { accept: accept as string[] })}
                />
                <span>
                  <DraftInput
                    className={css.input}
                    aria-label="Slot size limit in MB"
                    placeholder="default"
                    inputMode="numeric"
                    value={slot.maxBytes}
                    format={formatMb}
                    parse={parseMb}
                    onCommit={(maxBytes) => {
                      const { maxBytes: _old, ...rest } = slot;
                      const slots = [...type.slots];
                      slots[i] = maxBytes === undefined ? rest : { ...rest, maxBytes: maxBytes as number };
                      setSlots(type, slots);
                    }}
                  />
                  {mbInvalid && <span className={css.refuse}>1–100</span>}
                </span>
                <input
                  type="checkbox"
                  aria-label="Slot required"
                  checked={slot.required}
                  onChange={(e) => patchSlot(type, i, { required: e.target.checked })}
                />
                <span>
                  <button
                    type="button"
                    className={css.miniButton}
                    aria-label="Move up"
                    disabled={i === 0}
                    onClick={() => move(type, i, -1)}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    className={css.miniButton}
                    aria-label="Move down"
                    disabled={i === type.slots.length - 1}
                    onClick={() => move(type, i, 1)}
                  >
                    ▼
                  </button>
                </span>
                <button
                  type="button"
                  className={css.miniButton}
                  onClick={() => patchSlot(type, i, { retired: !slot.retired })}
                >
                  {slot.retired ? "RESTORE" : "RETIRE"}
                </button>
              </div>
            );
          })}

          <Button
            variant="secondary"
            size="rail"
            style={{ marginTop: "var(--space-16)" }}
            onClick={() => addSlot(type)}
          >
            + ADD FORMAT
          </Button>
        </div>
      ))}
    </section>
  );
}
